import React from 'react';

import { css } from '@emotion/react';
import { Box, Icons, PressableBox, Text } from '@opendoor/bricks-next';

import AccountMenu from 'components/account/AccountMenu';
import { LoginSection } from 'components/landing-pages-v2/shared/header/LoginSection';
import { defaultEaseCss } from 'components/landing-pages-v2/shared/motion/MotionVariants';

import { useT } from 'helpers/i18n';

interface AccountSectionProps {
  isLoggedIn: boolean;
  showLogin: boolean;
  firstName: string;
  initials: string[] | (string | undefined)[];
  isExpanded: boolean;
  handleAccountClick: () => void;
}

export const AccountSection: React.FC<AccountSectionProps> = ({
  isLoggedIn,
  showLogin,
  firstName,
  initials,
  isExpanded,
  handleAccountClick,
}) => {
  const { t } = useT();

  return (
    <PressableBox
      onPress={handleAccountClick}
      position="relative"
      display="flex"
      justifyContent="center"
      alignItems="center"
      aria-label="Account menu"
      analyticsName="account-section-click"
    >
      {isLoggedIn ? (
        <PressableBox
          display="inline-flex"
          cursor="pointer"
          gap="$1x"
          aria-label="User account menu"
          analyticsName="user-account-menu-click"
        >
          <Box>
            <Text
              tag="span"
              $smallerThanMD={{ display: 'none' }}
              typography="$labelMedium"
              color="$contentPrimary"
              hoverStyle={{
                color: '$backgroundAccentBrand',
              }}
            >
              {t('homepage.titles.welcome', 'Welcome')} {firstName}
            </Text>
            <Text
              tag="span"
              typography="$labelSmall"
              $largerThanSM={{ display: 'none' }}
              borderRadius="$60x"
              backgroundColor="$backgroundInversePrimary"
              color="$contentInversePrimary"
              p="$2x"
            >
              {initials}
            </Text>
          </Box>
          <Box
            width={24}
            height={24}
            position="relative"
            $smallerThanMD={{ top: 3 }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{
              transition: `0.15s ${defaultEaseCss} transform`,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
              transformOrigin: 'center',
            }}
          >
            <Icons.ChevronDown size={20} color="$contentPrimary" />
          </Box>
          <Box
            position="absolute"
            top={-35}
            css={css`
              right: -35px;
              @media (max-width: 1201px) {
                right: -30px;
              }
              @media (max-width: 767px) {
                right: -20px;
                left: inherit;
              }
            `}
          >
            <AccountMenu isExpanded={isExpanded} isLoggedIn={isLoggedIn} />
          </Box>
        </PressableBox>
      ) : (
        <LoginSection showLogin={showLogin} />
      )}
    </PressableBox>
  );
};
